// IP 合作谈判表格共享单元格渲染（核心商务条款 / IP合作游戏方详情 / 权益明细 复用）。
// 回复列 ActionCell / 意见反馈列 OpinionCell 为模块顶层稳定组件（identity 稳定），
// 作为 ProTable customComp 引用，避免 inline 闭包被当作组件类型渲染时卸载重挂载、
// 导致 TextAreaPopover 等有状态单元格状态丢失（popover 打开后立即关闭）。
// 批量编辑标题 createRenderBatchEditTitle（列头 Popover，仅作用于本表格 itemKeys）。
// 数据收集统一走外层 editState（key=item_key），提交时由外层聚合。

import React from 'react';
import { Popover, Space, Tag } from '@bedrock/components';
import { Edit } from '@bedrock/icons-react';
import TextAreaPopover from '@mpc/common/src/components/SForm/SFormElement/TextAreaPropover';
import type { IpCooperationNegotiationItemVo } from '#services/api';
import { isOuterIpService } from '@mpc/common/src/utils/sType';
import ActionTagCell, { ACTION_TAG_COLOR } from './ActionTagCell';
import RoundDiffCell, { OLD_VALUE_CLASS } from '../RoundDiff/RoundDiffCell';
import ActionDiffCell from '../RoundDiff/ActionDiffCell';
import type { RoundDiffValue } from '../RoundDiff/types';
import DurationInput, {
  formatDurationValue,
  type DurationValue,
} from '../DurationInput';

/** 谈判条款行（IpCooperationNegotiationItemVo + 前端扩展 offer_value；后端未定义该字段，mock/扩展用） */
export type NegotiationItem = IpCooperationNegotiationItemVo & {
  offer_value?: string;
};

/** 单条条款的编辑状态（外层维护，key=item_key） */
export interface NegotiationEditState {
  /** 版权方操作 ACCEPT/MODIFY/REJECT */
  action?: string;
  /** 修改意见 / 拒绝原因（≤300 字） */
  opinion?: string;
}

// 批量编辑操作（Popover 展示三个选项）
export const BATCH_ACTION_OPTIONS = [
  { name: '全部接受', value: 'ACCEPT' },
  { name: '全部修改', value: 'MODIFY' },
  { name: '全部拒绝', value: 'REJECT' },
];

// 意见反馈列标题（表格列头 + TextAreaPopover 弹层标题统一）
export const OPINION_TITLE = '意见反馈/说明';
// 外包 IP 服务场景列标题追加必填提示（对齐 sType.getOuterTitle 外包/内包差异化范式）
const OPINION_TITLE_OUTER = '意见反馈/说明 （如修改或拒绝则必填）';
/** 意见反馈列标题取值：外包 IP 服务场景带「如修改或拒绝则必填」提示，内包返回基础标题 */
export const getOpinionTitle = () =>
  isOuterIpService() ? OPINION_TITLE_OUTER : OPINION_TITLE;

/** 单元格渲染上下文（各表格从 props 组装后传入） */
export interface CellContext {
  /** 详情条款按 item_key 索引（回显后端 action/opinion/offer_value） */
  itemsByKey: Record<string, NegotiationItem>;
  /** 编辑状态（外层 editState） */
  editState: Record<string, NegotiationEditState>;
  /** 是否可编辑（当前轮次 + 版权方待确认） */
  editable: boolean;
  /** 单条编辑变更回调 */
  onEditChange?: (
    itemKey: string,
    patch: Partial<NegotiationEditState>
  ) => void;
  /** 批量操作（Popover 选操作后整批应用；itemKeys 限定当前表格实例） */
  onBatchAction?: (action: string, itemKeys?: string[]) => void;
  /** 联动上线时长变更回调（SolutionTable 受控字段 solution_table 写回；按行 index_id 定位） */
  onDurationChange?: (indexId?: number, value?: DurationValue) => void;
}

/** 回复列表头：* 回复 + 批量编辑 Popover（Popover 内三个 Tag 点击操作；仅针对当前表格实例 itemKeys） */
export const createRenderBatchEditTitle =
  ({ editable, onBatchAction }: CellContext) =>
  (itemKeys: string[]) => (
    <span className="inline-flex items-center gap-1">
      {/* 走查：只读状态不展示必填红 *（编辑态才显示） */}
      {editable && <span className="text-[#ff4d4f]">*</span>} 回复
      {editable && onBatchAction && (
        <Popover
          placement="bottom"
          trigger="click"
          content={
            <Space direction="vertical" size={4}>
              {BATCH_ACTION_OPTIONS.map((opt) => (
                <span
                  key={opt.value}
                  className="cursor-pointer"
                  onClick={() => onBatchAction(opt.value, itemKeys)}
                >
                  <Tag
                    size="small"
                    text={opt.name}
                    color={ACTION_TAG_COLOR[opt.value]}
                  />
                </span>
              ))}
            </Space>
          }
        >
          <a className="ml-1 inline-flex items-center gap-0.5 text-[#1677ff]">
            <Edit size={14} />
            批量编辑
          </a>
        </Popover>
      )}
    </span>
  );

/** 单元格自定义渲染的上下文 props（随 row 显式传入稳定组件，触发 cellEditMemo 引用比较重渲染） */
export interface CellRenderProps {
  /** 当前行数据 */
  row: any;
  /** 详情条款按 item_key 索引（回显后端 action/opinion/offer_value） */
  itemsByKey: Record<string, NegotiationItem>;
  /** 编辑状态（外层 editState） */
  editState: Record<string, NegotiationEditState>;
  /** 是否可编辑（⚠️ 会被 TableExtend custom-component 注册用 ProTable 表格级 editable 覆盖，实际值不可信） */
  editable: boolean;
  /** 真实只读态（独立 prop 不被 TableExtend 覆盖；readOnly=true 时强制只读，优先于 editable） */
  readOnly?: boolean;
  /** 单条编辑变更回调 */
  onEditChange?: (
    itemKey: string,
    patch: Partial<NegotiationEditState>
  ) => void;
  /** 历史轮次对比态：版权方回复 diff（仅 readOnly 时生效；非对比态缺省） */
  actionDiff?: RoundDiffValue;
  /** 历史轮次对比态：意见反馈/说明 diff（仅 readOnly 时生效；非对比态缺省） */
  opinionDiff?: RoundDiffValue;
}

/**
 * 回复列稳定组件（模块顶层，identity 稳定）。
 * 用顶层组件而非 inline 闭包作 ProTable customComp：ProTable 每次重渲染 editRender 都会新建
 * inline 箭头函数，被当作组件类型渲染时 React 判定类型变化 → 单元格子树卸载重挂载 →
 * 内部有状态组件（TextAreaPopover 的 visible）状态丢失（popover 打开后立即关闭）。
 * 上下文（row/editState/itemsByKey）经 props 显式传入，cellEditMemo 对非函数 prop 引用比较
 * → 上下文变化时重渲染（复用实例）；onEditChange 为函数 prop 被跳过比较，React 复用 fiber 时 props 替换为最新。
 */
export const ActionCell: React.FC<CellRenderProps> = ({
  row,
  itemsByKey,
  editState,
  editable,
  readOnly,
  onEditChange,
  actionDiff,
}) => {
  // readOnly 为真实只读态（不被 TableExtend 覆盖）；editable 可能被 ProTable 覆盖，仅作兜底
  const isReadOnly = readOnly ?? !editable;
  const st = editState[row.item_key] || {};
  // 优先级：editState（用户编辑）> 行字段（详情扁平化注入 solution_table/benefit_rows 的 action）> itemsByKey（negotiation_items 兜底）
  const action =
    st.action || row.action || itemsByKey[row.item_key]?.action || '';
  // 历史轮次对比态：readOnly + 有 action diff → 复用 ActionDiffCell（新值 Tag 按 diff 类型着色 + 旧值 Tag light-grey）
  if (actionDiff && isReadOnly) {
    return <ActionDiffCell diff={actionDiff} newAction={action} />;
  }
  return (
    <ActionTagCell
      value={action}
      editable={!isReadOnly}
      onChange={(a) => onEditChange?.(row.item_key, { action: a })}
    />
  );
};

/** 意见反馈列稳定组件（顶层组件 identity 稳定，解决 ProTable 重渲染卸载导致 popover 立即关闭，见 ActionCell 注释） */
export const OpinionCell: React.FC<CellRenderProps> = ({
  row,
  itemsByKey,
  editState,
  editable,
  readOnly,
  onEditChange,
  opinionDiff,
}) => {
  // readOnly 为真实只读态（不被 TableExtend 覆盖）；editable 可能被 ProTable 覆盖，仅作兜底
  const isReadOnly = readOnly ?? !editable;
  const st = editState[row.item_key] || {};
  // 优先级：editState（用户编辑）> 行字段（详情扁平化注入 solution_table/benefit_rows 的 opinion）> itemsByKey（negotiation_items 兜底）
  const value =
    st.opinion ?? row.opinion ?? itemsByKey[row.item_key]?.opinion ?? '';
  // 历史轮次对比态：readOnly + 有 opinion diff → 渲染最新值 + 历史值（灰删除线），保留换行
  if (opinionDiff && isReadOnly) {
    return (
      <RoundDiffCell
        diff={opinionDiff}
        renderNewValue={(v) => (
          <span className="whitespace-pre-line">{String(v ?? '') || '--'}</span>
        )}
        renderOldValue={(v) => (
          <span className="text-[#BFBFBF] line-through whitespace-pre-line">
            {String(v ?? '') || '--'}
          </span>
        )}
      />
    );
  }
  return (
    <TextAreaPopover
      title={getOpinionTitle()}
      value={value}
      onChange={(v: any) => onEditChange?.(row.item_key, { opinion: v })}
      view={isReadOnly}
      // 展示态保留换行（外包走查：版权方填写的多行意见反馈需换行展示，原 showLine=1 被 WebkitLineClamp 压成单行）
      showLine={2}
      showTooltip={false}
      defaultChildrenProps={{ size: 'small', placeholder: '请输入' }}
      // 意见反馈/说明 ≤300 字（PRD 约束）；TextAreaPopover 公共组件默认 maxLength=500，
      // 此处经 textAreaProps 透传覆盖（组件内部 {...textAreaProps} 在默认 maxLength 之后展开）
      textAreaProps={{ maxLength: 300 }}
    />
  );
};
